import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import dataService from '../services/dataService';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  pageType,
  pageSlug
}) => {
  const { settings: contextSettings } = useData();
  const [settings, setSettings] = useState(contextSettings || {});
  const [pageSEO, setPageSEO] = useState(null);
  const [seoLoaded, setSeoLoaded] = useState(false);

  // Load settings if not available from context
  useEffect(() => {
    if (!contextSettings || Object.keys(contextSettings).length === 0) {
      dataService.getSettings()
        .then(loadedSettings => {
          console.log('Loaded settings directly:', loadedSettings);
          setSettings(loadedSettings);
        })
        .catch(error => {
          console.error('Failed to load settings:', error);
        });
    } else {
      setSettings(contextSettings);
    }
  }, [contextSettings]);

  // Fetch page-specific SEO data
  useEffect(() => {
    if (pageType) {
      dataService.getSEO(pageType, pageSlug)
        .then(seoData => {
          console.log(`SEO data loaded for ${pageType}:`, seoData);
          setPageSEO(seoData);
          setSeoLoaded(true);
        })
        .catch(error => {
          console.error('Failed to load SEO data:', error);
          setSeoLoaded(true);
        });
    } else {
      setSeoLoaded(true);
    }
  }, [pageType, pageSlug]);

  // Use page-specific SEO data if available, otherwise fall back to props or settings
  const pageTitle = pageSEO?.meta_title || title || settings.default_meta_title || "Jiva Nadi Therapy & Chiropractor Center";
  const pageDescription = pageSEO?.meta_description || description || settings.default_meta_description || "Experience holistic healing with expert chiropractic care, Ayurveda, and naturopathy treatments.";
  const pageKeywords = pageSEO?.meta_keywords || keywords || 'chiropractor Mumbai, pain relief, non-surgical treatment, spine care, JIVA NADI, Dr. Saurabh Singh';
  const pageImage = image || pageSEO?.og_image || settings.og_image || "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1200&h=630&fit=crop";
  const pageUrl = url || settings.site_url || window.location.href;
  const siteName = settings.site_name || "Jiva Nadi Therapy Center";

  // Google Tag Manager integration
  useEffect(() => {
    console.log('SEO Component - Settings check:', {
      google_analytics_id: settings.google_analytics_id,
      google_search_console: settings.google_search_console,
      bing_webmaster: settings.bing_webmaster,
      gtag_exists: !!window.gtag
    });

    // Initialize GTM if not already loaded
    if (settings.google_analytics_id && !window.gtag) {
      console.log('Loading Google Analytics with ID:', settings.google_analytics_id);
      // Load Google Analytics 4
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${settings.google_analytics_id}`;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${settings.google_analytics_id}');
      `;
      document.head.appendChild(script2);
    }

    // Google Search Console verification
    if (settings.google_search_console && !document.querySelector(`meta[name="google-site-verification"][content="${settings.google_search_console}"]`)) {
      const metaTag = document.createElement('meta');
      metaTag.name = 'google-site-verification';
      metaTag.content = settings.google_search_console;
      document.head.appendChild(metaTag);
    }

    // Bing Webmaster verification
    if (settings.bing_webmaster && !document.querySelector(`meta[name="msvalidate.01"][content="${settings.bing_webmaster}"]`)) {
      const metaTag = document.createElement('meta');
      metaTag.name = 'msvalidate.01';
      metaTag.content = settings.bing_webmaster;
      document.head.appendChild(metaTag);
    }
  }, [settings]);

  // Update meta tags when pageSEO changes
  useEffect(() => {
    if (pageSEO) {
      console.log('PageSEO loaded, updating meta tags:', pageSEO);
      // Force re-render of meta tags
      setSeoLoaded(true);
    }
  }, [pageSEO]);

  useEffect(() => {
    if (!seoLoaded) return;

    console.log('Updating meta tags:', {
      pageTitle,
      pageDescription,
      pageSEO: !!pageSEO,
      pageType
    });

    // Update document title
    document.title = pageTitle;

    // Update meta tags
    const updateMetaTag = (name, content, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.setAttribute('content', content);
        document.getElementsByTagName('head')[0].appendChild(element);
      }
    };

    // Basic meta tags
    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', pageKeywords);
    updateMetaTag('author', siteName);

    // Open Graph tags
    updateMetaTag('og:type', pageSEO?.og_type || type, true);
    updateMetaTag('og:title', pageSEO?.og_title || pageTitle, true);
    updateMetaTag('og:description', pageSEO?.og_description || pageDescription, true);
    updateMetaTag('og:image', pageImage, true);
    updateMetaTag('og:url', pageUrl, true);
    updateMetaTag('og:site_name', siteName, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', pageSEO?.twitter_card || 'summary_large_image');
    updateMetaTag('twitter:title', pageSEO?.twitter_title || pageTitle);
    updateMetaTag('twitter:description', pageSEO?.twitter_description || pageDescription);
    updateMetaTag('twitter:image', pageSEO?.twitter_image || pageImage);

    // Robots directive
    if (pageSEO?.robots_directive) {
      updateMetaTag('robots', pageSEO.robots_directive);
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', pageSEO?.canonical_url || pageUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', pageSEO?.canonical_url || pageUrl);
      document.getElementsByTagName('head')[0].appendChild(canonicalLink);
    }

  }, [pageTitle, pageDescription, pageKeywords, pageImage, pageUrl, siteName, type, pageSEO, seoLoaded]);

  return null; // This component doesn't render anything
};

export default SEO;